import type { HTMLAttributes } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import { Slot } from '@radix-ui/react-slot';
import { useFiltersTheme } from '../../filters-theme-provider';
import { FiltersGroup } from '../layout';
import { verticalScrollBar } from '../scrollbar-mixin';
import { CloseIconButton } from './close-button/close-icon-button';
import type { FilterModalContentProps } from './types';

type ModalContentPropsWithClose = HTMLAttributes<HTMLDivElement> &
  Omit<FilterModalContentProps, 'onOpenRequest'>;

export function ModalContent({
  children,
  title,
  displayClearButton,
  clearButton,
  applyButton,
  onCloseRequest,
  onApplyRequest,
  onDiscardRequest,
  onClearRequest,
  ...forwardingProps
}: ModalContentPropsWithClose) {
  const { modalBackground, textColor } = useFiltersTheme();

  return (
    <Content
      {...forwardingProps}
      $backgroundColor={modalBackground}
      $textColor={textColor}
    >
      <ContentHeader>
        {title}
        <CloseIconButton
          onClick={() => {
            onCloseRequest();
            onDiscardRequest();
          }}
        />
      </ContentHeader>
      <ContentBody>{children}</ContentBody>
      <ContentFooter>
        {displayClearButton && (
          <Slot
            onClick={() => {
              onClearRequest();
            }}
          >
            {clearButton}
          </Slot>
        )}
        <Slot
          onClick={() => {
            onApplyRequest();
            onCloseRequest();
          }}
        >
          {applyButton}
        </Slot>
      </ContentFooter>
    </Content>
  );
}

const ContentHeader = styled.div.attrs({
  className: createReadableClass({
    component: 'modal',
    element: 'content-header'
  })
})`
  padding: 14px 20px;
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ContentBody = styled.div.attrs({
  className: createReadableClass({
    component: 'modal',
    element: 'content-body'
  })
})`
  padding: 0;
  flex-grow: 1;
  overflow-y: auto;

  & > ${FiltersGroup} {
    padding: 16px 20px 20px;
  }
`;

const ContentFooter = styled.div.attrs({
  className: createReadableClass({
    component: 'modal',
    element: 'content-footer'
  })
})`
  display: flex;
  padding: 16px 20px;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;

  & > *:only-child {
    margin-left: auto;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap-reverse;
    & > * {
      flex-grow: 1;
    }
  }
`;

const Content = styled.div.attrs({
  className: createReadableClass({
    component: 'modal',
    element: 'content'
  })
})<{ $backgroundColor: string; $textColor: string }>`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  height: 100%;
  background: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $textColor }) => $textColor};

  ${ContentBody} {
    ${({ $textColor }) => verticalScrollBar(getColorWithAlpha($textColor, 0.2))}
    & > ${FiltersGroup}:not(:last-child) {
      border-bottom: 1px solid
        ${({ $textColor }) => getColorWithAlpha($textColor, 0.2)};
    }
  }

  ${ContentHeader} {
    color: ${({ $textColor }) => $textColor};
    border-bottom: 1px solid
      ${({ $textColor }) => getColorWithAlpha($textColor, 0.2)};
  }

  ${ContentFooter} {
    border-top: 1px solid
      ${({ $textColor }) => getColorWithAlpha($textColor, 0.2)};
  }
`;
